import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, Phone, User, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import AuthLayout from "@/components/AuthLayout";

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", phone: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/signup", form);
      // If dev mode, backend returns dev_otp — stash it to show as hint
      const devOtp = data?.otp_delivery?.dev_otp || null;
      toast.success("Verification code sent");
      navigate("/verify", { state: { email: form.email, devOtp } });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      step="[ auth_02 ] sign up"
      title="Create your account"
      subtitle="Email, password, and phone — we'll send a 6-digit code to verify your email."
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-2">
          <label className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 flex items-center gap-2">
            <User size={11} /> full name (optional)
          </label>
          <Input
            value={form.name}
            onChange={upd("name")}
            placeholder="Ada Lovelace"
            className="rounded-none bg-[#0a0a0a] border-neutral-800 focus-visible:border-[#E2FF54] focus-visible:ring-0 mono h-12 text-white"
            data-testid="signup-name"
          />
        </div>
        <div className="space-y-2">
          <label className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 flex items-center gap-2">
            <Mail size={11} /> email
          </label>
          <Input
            type="email"
            required
            value={form.email}
            onChange={upd("email")}
            placeholder="you@example.com"
            className="rounded-none bg-[#0a0a0a] border-neutral-800 focus-visible:border-[#E2FF54] focus-visible:ring-0 mono h-12 text-white"
            data-testid="signup-email"
          />
        </div>
        <div className="space-y-2">
          <label className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 flex items-center gap-2">
            <Phone size={11} /> phone number
          </label>
          <Input
            type="tel"
            required
            value={form.phone}
            onChange={upd("phone")}
            placeholder="+1 555 123 4567"
            className="rounded-none bg-[#0a0a0a] border-neutral-800 focus-visible:border-[#E2FF54] focus-visible:ring-0 mono h-12 text-white"
            data-testid="signup-phone"
          />
        </div>
        <div className="space-y-2">
          <label className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 flex items-center gap-2">
            <Lock size={11} /> password
          </label>
          <Input
            type="password"
            required
            value={form.password}
            onChange={upd("password")}
            placeholder="min 6 characters"
            className="rounded-none bg-[#0a0a0a] border-neutral-800 focus-visible:border-[#E2FF54] focus-visible:ring-0 mono h-12 text-white"
            data-testid="signup-password"
          />
        </div>

        {error && (
          <div
            className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-3 mono text-xs text-[#FF9994]"
            data-testid="signup-error"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="btn-brutal w-full rounded-none bg-[#E2FF54] text-black hover:bg-[#d4f531] mono uppercase tracking-[0.24em] text-xs py-6"
          data-testid="signup-submit"
        >
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin mr-2" /> sending code…
            </>
          ) : (
            <>
              send verification code <ArrowRight size={14} className="ml-2" />
            </>
          )}
        </Button>
      </form>

      <div className="mt-8 pt-6 border-t border-neutral-900 mono text-xs text-neutral-500 flex items-center justify-between">
        <span>already have an account?</span>
        <Link
          to="/login"
          className="text-[#E2FF54] hover:underline uppercase tracking-[0.2em] text-[11px]"
          data-testid="signup-goto-login"
        >
          sign in →
        </Link>
      </div>
    </AuthLayout>
  );
}
